// One person's canvas areas — the dropdown on each row of the Today tab
// (Oct 19, 2026). Greg: "have a drop down on each team member so a manager can
// see the assigned areas per team member and click a button to add."
//
// Shows the areas this person is on with THEIR numbers in each, and (for crew
// managers) lets the manager take them off an area, put them on another
// existing area, or open the canvas map in draw mode with them pre-picked to
// lasso a brand-new one. Every write goes through lib/canvasAreas (RPCs, RLS
// = canvas_can_manage); `onChanged` refetches the board's area stats.

import { useMemo, useState } from "react";
import {
  addAreaMember, areaMapHref, areaName, areaPlace, coverage, doorsLeft, drawAreaHref,
  firstName, relTime, removeAreaMember, type AreaStats,
} from "../../../lib/canvasAreas";
import { areaDayState, sortAreasForDay } from "../../../lib/dailyAreas";
import { dayLabel, todayET } from "../../../lib/dailyCommitments";
import { HailChip, PinStateBar } from "./AreaBits";

interface Person { id: string; full_name: string; role: string }

export default function PersonAreasPanel({ person, areas, canWrite, onChanged }: {
  person: Person;
  /** Every active area (the board's fetch); filtered to this person here. */
  areas: AreaStats[];
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const first = firstName(person.full_name);

  const today = todayET();
  const mine = useMemo(
    () => sortAreasForDay(areas.filter((a) => a.reps.some((r) => r.user_id === person.id)), today),
    [areas, person.id, today],
  );
  const others = useMemo(
    () => areas
      .filter((a) => !a.reps.some((r) => r.user_id === person.id))
      .sort((a, b) => doorsLeft(b.states) - doorsLeft(a.states)),
    [areas, person.id],
  );

  async function add(a: AreaStats) {
    setBusy(a.area_group_id); setMsg(null);
    try {
      const r = await addAreaMember(a.area_group_id, person.id);
      setMsg(r.already ? `${first} was already on ${areaName(a)}.` : `${first} added to ${areaName(a)}.`);
      setAdding(false);
      onChanged();
    } catch (e) {
      setMsg((e as Error).message);
    } finally { setBusy(null); }
  }

  async function remove(a: AreaStats) {
    const alone = a.reps.length <= 1;
    const ok = window.confirm(alone
      ? `${first} is the only one on ${areaName(a)}. Taking ${first} off ends the area — its pins go back to the open pool. Continue?`
      : `Take ${first} off ${areaName(a)}?`);
    if (!ok) return;
    setBusy(a.area_group_id); setMsg(null);
    try {
      const r = await removeAreaMember(a.area_group_id, person.id);
      setMsg(r.areaStillActive ? `${first} is off ${areaName(a)}.` : `${areaName(a)} ended — nobody is on it now.`);
      onChanged();
    } catch (e) {
      setMsg((e as Error).message);
    } finally { setBusy(null); }
  }

  return (
    <div className="space-y-2 border-t border-sky-100 bg-sky-50/40 px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold text-slate-800">
          📐 {first}'s canvas areas
          <span className="ml-1 font-normal text-slate-500">· live</span>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => { setAdding((v) => !v); setMsg(null); }}
              className={`rounded-md px-3 py-1.5 text-xs font-bold ${adding ? "border border-slate-300 bg-white text-slate-600" : "bg-sky-600 text-white"}`}>
              {adding ? "Close" : "＋ Add area"}
            </button>
          </div>
        )}
      </div>

      {msg && <div className="rounded-md bg-white px-2 py-1.5 text-xs text-slate-700 ring-1 ring-slate-200">{msg}</div>}

      {mine.length === 0 && !adding && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-3 text-xs text-slate-500">
          {first} isn't assigned to an area.
          {canWrite ? " Add one so it's outlined on their canvas map and shows on their home page." : ""}
        </div>
      )}

      {mine.map((a) => {
        const me = a.reps.find((r) => r.user_id === person.id)!;
        const teammates = a.reps.filter((r) => r.user_id !== person.id);
        const place = areaPlace(a);
        return (
          <div key={a.area_group_id} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="truncate text-sm font-bold text-slate-900">{areaName(a)}</div>
                  {areaDayState(a, today) === "today" && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-800">Today</span>}
                  {areaDayState(a, today) === "past" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">Still open from {dayLabel(a.for_date!)}</span>}
                  {areaDayState(a, today) === "future" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">For {dayLabel(a.for_date!)}</span>}
                </div>
                <div className="truncate text-[11px] text-slate-500">
                  {areaName(a) !== place && place ? `📍 ${place} · ` : ""}
                  {a.pin_count.toLocaleString()} pins · {doorsLeft(a.states).toLocaleString()} doors left · {Math.round(coverage(a) * 100)}% knocked
                  {teammates.length ? ` · with ${teammates.map((t) => firstName(t.full_name)).join(", ")}` : ""}
                </div>
              </div>
              <HailChip hail={a.hail} />
            </div>
            <div className="mt-2"><PinStateBar states={a.states} total={a.pin_count} className="h-1.5" /></div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
              <span><b className="tabular-nums text-slate-900">{me.knocks_today}</b> knocks today</span>
              <span><b className="tabular-nums text-slate-900">{me.knocks_7d}</b> knocks · <b className="tabular-nums text-purple-700">{me.talked_7d}</b> talked (7d)</span>
              <span><b className="tabular-nums text-slate-900">{me.appts_since}</b> appts since added</span>
              <span>Last knock {relTime(me.last_knock_at)}</span>
              <span className="text-slate-400">Team 7d: {a.activity.knocks_7d} knocks</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <a href={areaMapHref(a.area_group_id)} target="_blank" rel="noopener"
                className="rounded-md bg-orange-50 px-2.5 py-1 text-[11px] font-bold text-orange-700 hover:bg-orange-100">
                🗺 Open on map ↗
              </a>
              {canWrite && (
                <button type="button" onClick={() => remove(a)} disabled={busy === a.area_group_id}
                  className="rounded-md border border-rose-200 bg-white px-2.5 py-1 text-[11px] font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                  {busy === a.area_group_id ? "Removing…" : `Take ${first} off`}
                </button>
              )}
            </div>
          </div>
        );
      })}

      {adding && canWrite && (
        <div className="rounded-lg border-2 border-sky-300 bg-white p-3">
          <div className="mb-2 text-[11px] font-semibold text-slate-500">
            Put {first} on an existing area — they share it with whoever's already there:
          </div>
          {others.length === 0 ? (
            <div className="text-xs text-slate-500">No other active areas right now.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {others.map((a) => (
                <li key={a.area_group_id} className="flex flex-wrap items-center gap-2 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-bold text-slate-900">{areaName(a)}</div>
                    <div className="truncate text-[11px] text-slate-500">
                      {a.pin_count.toLocaleString()} pins · {doorsLeft(a.states).toLocaleString()} doors left
                      {a.reps.length ? ` · ${a.reps.map((r) => firstName(r.full_name)).join(", ")}` : ""}
                    </div>
                  </div>
                  <button type="button" onClick={() => add(a)} disabled={busy === a.area_group_id}
                    className="rounded-md bg-slate-900 px-3 py-1 text-[11px] font-bold text-white disabled:opacity-50">
                    {busy === a.area_group_id ? "Adding…" : `Add ${first}`}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <a href={drawAreaHref(person.id)} target="_blank" rel="noopener"
            className="mt-2 inline-block rounded-md border border-sky-300 px-3 py-1.5 text-[11px] font-bold text-sky-700 hover:bg-sky-50">
            ✏️ Draw a new area for {first} on the map ↗
          </a>
        </div>
      )}
    </div>
  );
}
